import { useState } from "react"
import Popover from "@mui/material/Popover"
import { DateCalendar, LocalizationProvider, pickersMonthClasses } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import { Calendar } from "lucide-react"
import { Field } from "../../ui"
import { orderForDateLabel, yearMonthOfOrderForDate } from "../../constants"

let idSeq = 0

const OrderForDatePicker = ({ label, value, error, hint, stayOpen, markedDates = [], onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [round, setRound] = useState(0)
  // Mirrors the calendar's date, because month buttons aren't told which year they show.
  const [shownDate, setShownDate] = useState(null)
  const [id] = useState(() => `pc-order-for-date-${(idSeq += 1)}`)

  const handleOpen = (e) => {
    if (value) setShownDate(dayjs(value))
    setAnchorEl(e.currentTarget)
  }

  const handleCalendarChange = (date, selectionState) => {
    setShownDate(date)
    // Month and year picks are "partial"; only a day pick is a real value.
    if (selectionState !== "finish") return
    if (stayOpen) {
      setRound((r) => r + 1)
    } else {
      setAnchorEl(null)
    }
    onChange(date.format("YYYY-MM-DD"))
  }

  const shownYear = (shownDate ?? dayjs()).year()
  const markedMonths = markedDates.map(yearMonthOfOrderForDate)
  const monthButtonProps = ({ value: month }) =>
    markedMonths.includes(`${shownYear}-${String(month + 1).padStart(2, "0")}`)
      ? { className: pickersMonthClasses.selected, "aria-checked": true }
      : {}

  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <button
        id={id}
        type="button"
        className="pc-control"
        aria-haspopup="dialog"
        aria-invalid={!!error || undefined}
        onClick={handleOpen}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left", cursor: "pointer" }}
      >
        <span style={value ? undefined : { color: "var(--pc-faint)" }}>{value ? orderForDateLabel(value) : "Select date"}</span>
        <Calendar aria-hidden size={16} />
      </button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        // Beside the field when staying open, so the list of picked dates below it stays visible.
        anchorOrigin={stayOpen ? { vertical: "top", horizontal: "right" } : { vertical: "bottom", horizontal: "left" }}
        // Theme makes Popover paper transparent, and --pc-* tokens don't reach this portal.
        PaperProps={{ sx: { backgroundColor: "#fff" } }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Uncontrolled so a year pick moves the month view to that year.
              Remounting per round returns to the month view, in the last picked year. */}
          <DateCalendar
            key={round}
            views={["year", "month", "day"]}
            openTo="month"
            defaultValue={value ? dayjs(value) : null}
            referenceDate={shownDate ?? undefined}
            slotProps={{ monthButton: monthButtonProps }}
            onChange={handleCalendarChange}
          />
        </LocalizationProvider>
      </Popover>
    </Field>
  )
}

export default OrderForDatePicker
